import { useEffect, useCallback, type MouseEvent } from 'react';

interface UseModalDismissOptions {
  /** Escape 键关闭，默认开启 */
  esc?: boolean;
  /** 点击遮罩关闭，默认开启 */
  overlay?: boolean;
}

/**
 * 统一弹窗关闭交互：Escape 键 + 点击遮罩（仅当点击目标是遮罩本身时）。
 * 将返回值 spread 到遮罩容器上：
 *   <div className="modal-overlay" {...dismissProps}> ... </div>
 */
export function useModalDismiss(onClose: () => void, options?: UseModalDismissOptions) {
  const { esc = true, overlay = true } = options ?? {};

  useEffect(() => {
    if (!esc) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [esc, onClose]);

  const onOverlayClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (overlay && e.target === e.currentTarget) onClose();
  }, [overlay, onClose]);

  return { onOverlayClick };
}
